import React, {useEffect, useMemo, useRef} from 'react';
import {useSequentialModalContext} from '@basicComponents/sequential-modal/context';
import congratulationText from '../../assets/old-modal-congratulation.webp';
import winnerText from '../../assets/old-modal-winner_alert.webp';
import luckBuildText from '../../assets/old-modal-luck_building_up.webp';
import wonPrice from '../../assets/old-won-price.webp';
import bouns from '../../assets/daily-bouns.webp';

export enum ModalType {
  REGRESSION = 'regression',
  CASH_PRIZE = 'cash_prize',
  POINTS_REWARD = 'points_reward',
  INVITE_REWARD = 'invite_reward',
  WINNER_ALERT = 'winner_alert',
  LUCK_BUILDING = 'luck_building',
  DEFAULT = 'default',
}

interface OldUserModalProps {
  modalType: ModalType;
  amount?: number | string;
  rank?: number;
  userCount?: number;
}

interface ModalConfig {
  titleImage: any;
  displayText:
    | string
    | ((data: {
        rank?: number;
        userCount?: number;
        amount?: number | string;
      }) => string);
  rewardIcon: any;
  showAction: boolean;
  currency: string;
}

const modalConfig: Record<ModalType, ModalConfig> = {
  [ModalType.CASH_PRIZE]: {
    titleImage: congratulationText,
    displayText: (data: {rank?: number}) =>
      `You ranked ${data.rank} yesterday and won the prize.`,
    rewardIcon: wonPrice,
    showAction: true,
    currency: '₹',
  },
  [ModalType.POINTS_REWARD]: {
    titleImage: congratulationText,
    displayText: (data: {userCount?: number}) =>
      `You have successfully invited ${data.userCount} users and earned reward points.`,
    rewardIcon: bouns,
    showAction: true,
    currency: '',
  },
  [ModalType.INVITE_REWARD]: {
    titleImage: congratulationText,
    displayText: (data: {userCount?: number}) =>
      `The ${data.userCount} users you invited have completed the first recharge and received the reward points.`,
    rewardIcon: bouns,
    showAction: true,
    currency: '',
  },
  [ModalType.WINNER_ALERT]: {
    titleImage: winnerText,
    displayText: () =>
      "You've scored from the starter pool! JACKPOT DOUBLED!\n Bigger prizes await in Round 2!\n️ Battle on for MEGA REWARDS!\n Fortune favors the bold! ",
    rewardIcon: null,
    showAction: true,
    currency: '',
  },
  [ModalType.LUCK_BUILDING]: {
    titleImage: luckBuildText,
    displayText: () =>
      "SPECIAL POOL UNLOCKED!\n2X bigger rewards available\nReady for redemption? Let's go!",
    rewardIcon: null,
    showAction: true,
    currency: '',
  },
  [ModalType.REGRESSION]: {
    titleImage: congratulationText,
    displayText: 'Welcome back! Your bonus rate has been increased.',
    rewardIcon: null,
    showAction: true,
    currency: '',
  },
  [ModalType.DEFAULT]: {
    titleImage: congratulationText,
    displayText: 'Default message for other rewards',
    rewardIcon: null,
    showAction: false,
    currency: '',
  },
};

function OldUserModal({
  modalType = ModalType.DEFAULT,
  amount,
  rank,
  userCount,
}: OldUserModalProps) {
  const {goToNext} = useSequentialModalContext();

  const modalSettings = useMemo(() => {
    return modalConfig[modalType] || modalConfig[ModalType.DEFAULT];
  }, [modalType]);

  const getDisplayText = () => {
    if (typeof modalSettings.displayText === 'function') {
      return modalSettings.displayText({
        rank,
        userCount,
        amount,
      });
    }
    return modalSettings.displayText;
  };

  const handleGetItClick = () => {
    goToNext();
  };
  const timer = useRef<NodeJS.Timeout>();
  useEffect(() => {
    if (!modalSettings.showAction) {
      if (timer.current) {
        clearTimeout(timer.current);
        // eslint-disable-next-line no-void
        timer.current = void 0;
      }
      timer.current = setTimeout(() => {
        goToNext();
      }, 2 * 1000);
    }
  }, [modalSettings, goToNext]);

  return (
    <div>
      <div
        className="w-[23.4375rem] h-[363px] bg-full relative"
        style={{
          backgroundImage: `url(${require('../../assets/old-user-modal-bg.webp')})`,
        }}>
        <div
          className="absolute h-[79px] w-[303px] bg-center bg-contain bg-no-repeat left-1/2 -translate-x-1/2 top-[110px]"
          style={{
            backgroundImage: `url(${modalSettings.titleImage})`,
          }}
        />
        <div className="w-full h-[165px] absolute top-[170px] text-stroke-old-gold px-[50px] flex-col content-center items-center">
          <div className="text-base font-black text-center min-h-[57px] leading-5">
            {getDisplayText()}
          </div>
          <div className="flex items-center gap-3 justify-center">
            {modalSettings.rewardIcon && (
              <div
                className={
                  modalType === ModalType.CASH_PRIZE
                    ? 'size-[69px]'
                    : 'size-[61px]'
                }
                style={{
                  backgroundImage: `url(${modalSettings.rewardIcon})`,
                  backgroundSize: 'contain',
                  backgroundRepeat: 'no-repeat',
                }}
              />
            )}
            {amount ? (
              <div className="text-[31px] font-black">
                x {modalSettings.currency}
                {amount}
              </div>
            ) : null}
          </div>
        </div>
      </div>
      {modalSettings.showAction && (
        <div className="flex justify-center">
          <div
            className="rounded-[40px] border border-black/10 text-t1 text-xl font-bold capitalize w-[215px] h-[48px] flex justify-center items-center cursor-pointer"
            style={{
              background:
                'linear-gradient(180deg, #FFF472 0%, #FFF9CF 9.6%, #FFE95E 43.5%, #FFB200 73.72%, #FFDD45 88.46%, #FFCB00 100%)',
              boxShadow:
                '0 0 12px 0 rgba(255, 183, 0, 0.50), -2px 2px 2px 0 rgba(255, 255, 255, 0.70) inset',
            }}
            role="button"
            tabIndex={0}
            aria-label="Get reward"
            onKeyDown={e => e.key === 'Enter' && handleGetItClick()}
            onClick={handleGetItClick}>
            Get It
          </div>
        </div>
      )}
    </div>
  );
}

export default React.memo(OldUserModal);
